import { useEffect, useState } from "react";
import { useDataContext } from "../DataProvider/DataContext";
import { FormBorderStyle } from "../styledComponents/Components.styles";
import ECUCCataSelect from "./ECUCataSelect/ECUCCataSelect";
import { srpdParentLevelTemplate, srpTemplate, type SRPDModuleInformationInterface, type SRPDPreAssemblyImageInterface, type StateMachineListInterface, type StateSRPdParentLevelInterface } from "../../models/SRPSchema";
import ECUCataClusterSelect from "./ECUCataSelect/ECUCataClusterSelect";
import EditForm from "./EditForm/EditForm";
import { saveDataToLocalStorage } from "../../utilities/localStorageUtils";
import { initializeFromStorage, isValidString, isValidArray, isValidObject } from "../../utilities/persistOnPrefreshUtils";
import { SelectOption } from "../../utilities/dropDownOptions";
import { srpErrorsTemplate } from "../../models/SRPErrors";

export default function SRPForm() {
    const context = useDataContext();

    const { formProcessingState } = context;
    
    // Initialize state directly from localStorage with fallbacks (same pattern as DataProvider)
    const [projectKey, setProjectKey] = useState<string>(() => 
        initializeFromStorage("projectKey", "", isValidString)
    );
    const [moduleName, setModuleName] = useState<string>(() => 
        initializeFromStorage("moduleName", "", isValidString)
    );
    const [machineList, setMachineList] = useState<StateMachineListInterface[]>(() => 
        initializeFromStorage("machineList", structuredClone(srpTemplate.machineList), isValidArray)
    );
    const [moduleInformation, setModuleInformation] = useState<SRPDModuleInformationInterface>(() => 
        initializeFromStorage("moduleInformation", structuredClone(srpTemplate.moduleInformation), isValidObject)
    );
    const [preassemblyImage, setPreassemblyImage] = useState<SRPDPreAssemblyImageInterface>(() => 

        {
            const preAssemblyImageWithoutClusterList = structuredClone(srpTemplate.preassemblyImage);
            preAssemblyImageWithoutClusterList.clusterList = [];
            return initializeFromStorage("preassemblyImage", preAssemblyImageWithoutClusterList, isValidObject)
        }
    );
    const [preassemblyImageErrors, setPreassemblyImageErrors] = useState<any>(() => 
        initializeFromStorage("preassemblyImageErrors", structuredClone(srpErrorsTemplate.preassemblyImageErrors), isValidObject)
    );
    const [selectedKeyPersonOption, setSelectedKeyPersonOption] = useState<SelectOption>(() => 
        initializeFromStorage("selectedKeyPersonOption", undefined, isValidObject)
    );
    const [selectedResponsibleOptions, setSelectedResponsibleOptions] = useState<SelectOption[]>(() => 
        initializeFromStorage("selectedResponsibleOptions", [], isValidArray)
    );
    const [textFieldValues, setTextFieldValues] = useState<StateSRPdParentLevelInterface>(() => 
        initializeFromStorage("textFieldValues", structuredClone(srpdParentLevelTemplate), isValidObject)
    );
    const [textFieldErrors, setTextFieldErrors] = useState(() => 
        initializeFromStorage("textFieldErrors", structuredClone(srpErrorsTemplate.textFieldErrors), isValidObject)
    );
    const [machineListErrors, setMachineListErrors] = useState(() => 
        initializeFromStorage("machineListErrors", structuredClone(srpErrorsTemplate.machineListErrors), isValidArray)
    );
    const [moduleInformationErrors, setModuleInformationErrors] = useState(() => 
        initializeFromStorage("moduleInformationErrors", structuredClone(srpErrorsTemplate.moduleInformationErrors), isValidObject)
    );

    useEffect(() => {
      console.log("Data context:", context);
    }, [context]);


    // Save state changes to localStorage (same pattern as DataProvider)
    useEffect(() => {
        try {
            // Save all current state to localStorage whenever it changes
            saveDataToLocalStorage('textFieldValues', textFieldValues);
            saveDataToLocalStorage('textFieldErrors', textFieldErrors);
            saveDataToLocalStorage('moduleInformation', moduleInformation);
            saveDataToLocalStorage('moduleInformationErrors', moduleInformationErrors);
            saveDataToLocalStorage('machineList', machineList);
            saveDataToLocalStorage('machineListErrors', machineListErrors);
            saveDataToLocalStorage('preassemblyImage', preassemblyImage);
            saveDataToLocalStorage('preassemblyImageErrors', preassemblyImageErrors);
            saveDataToLocalStorage('projectKey', projectKey);
            saveDataToLocalStorage('moduleName', moduleName);
            saveDataToLocalStorage('selectedKeyPersonOption', selectedKeyPersonOption);
            saveDataToLocalStorage('selectedResponsibleOptions', selectedResponsibleOptions);
        } catch (error) {
            console.warn('Failed to save data to localStorage:', error);
        }

    }, [
        textFieldValues, textFieldErrors, moduleInformation, moduleInformationErrors,
        machineList, machineListErrors, preassemblyImage, preassemblyImageErrors,
        projectKey, moduleName, selectedKeyPersonOption, selectedResponsibleOptions,
    ]);


    const renderFormComponent = () => {
        if (formProcessingState === "ECUC_SELECT") {
            return (
                <ECUCCataSelect
                    selectedKeyPersonOption={selectedKeyPersonOption} 
                    setSelectedKeyPersonOption={setSelectedKeyPersonOption}
                    textFieldValues={textFieldValues}
                    setTextFieldValues={setTextFieldValues}
                    selectedResponsibleOptions={selectedResponsibleOptions}
                    setSelectedResponsibleOptions={setSelectedResponsibleOptions}
                    setMachineList={setMachineList}
                    setMachineListErrors={setMachineListErrors}
                    setModuleInformation={setModuleInformation}
                    preassemblyImage={preassemblyImage}
                    setPreassemblyImage={setPreassemblyImage}
                    moduleInformation={moduleInformation}
                    setProjectKey={setProjectKey}
                />
            );
        }
        
        if (formProcessingState === "CLUSTER_SELECT") {
            return (
                <ECUCataClusterSelect
                  selectedKeyPersonOption={selectedKeyPersonOption} 
                  setSelectedKeyPersonOption={setSelectedKeyPersonOption}
                  selectedResponsibleOptions={selectedResponsibleOptions}
                  setSelectedResponsibleOptions={setSelectedResponsibleOptions}
                  textFieldValues={textFieldValues}
                  setTextFieldValues={setTextFieldValues}
                  moduleInformation={moduleInformation}
                  machineList={machineList}
                  setMachineList={setMachineList}
                  preassemblyImage={preassemblyImage}
                  setPreassemblyImage={setPreassemblyImage}
                  setMachineListErrors={setMachineListErrors}
                  setProjectKey={setProjectKey}
                />
            );
        }
        
        return (
            <EditForm
                textFieldValues={textFieldValues}
                setTextFieldValues={setTextFieldValues}
                textFieldErrors={textFieldErrors}
                setTextFieldErrors={setTextFieldErrors}
                
                selectedKeyPersonOption={selectedKeyPersonOption}
                setSelectedKeyPersonOption={setSelectedKeyPersonOption}
                
                selectedResponsibleOptions={selectedResponsibleOptions}
                setSelectedResponsibleOptions={setSelectedResponsibleOptions}

                moduleInformation={moduleInformation}
                setModuleInformation={setModuleInformation}
                moduleInformationErrors={moduleInformationErrors}
                setModuleInformationErrors={setModuleInformationErrors}
                machineListErrors={machineListErrors}
                setMachineListErrors={setMachineListErrors}

                machineList={machineList}
                setMachineList={setMachineList}
                
                preassemblyImage={preassemblyImage}
                setPreassemblyImage={setPreassemblyImage}
                preassemblyImageErrors={preassemblyImageErrors}
                setPreassemblyImageErrors={setPreassemblyImageErrors}

                projectKey={projectKey}
                moduleName={moduleName}
                setProjectKey={setProjectKey}
                setModuleName={setModuleName}
              />
        );
    };
  
  return (
    <FormBorderStyle id="myForm">
        {renderFormComponent()}
    </FormBorderStyle>
  );
}